import type { BillingEntityDomainClaimStatus } from '@reflex/shared';
import type { Agent, AgentStatus, AgentActivity, BlueprintStatus, SecretSource } from '@/lib/types';
import { ACTIVE_STATUSES, NON_TERMINAL_STATUSES } from '@/lib/types';

/** Whether an agent is in a "live" state (devbox up, possibly resumable). */
export function isAgentActive(agent: Pick<Agent, 'status'>): boolean {
  return NON_TERMINAL_STATUSES.has(agent.status);
}

/**
 * Whether archiving this agent should prompt a confirmation first. Archiving is
 * non-destructive, but doing it to an agent that is mid-turn is easy to trigger
 * by accident, so we confirm while a turn is actually in flight — exactly when
 * the run also offers Stop ({@link isWorkingActivity}).
 *
 * `activity` is the derived working/idle signal. It matters because a run stays
 * at `status: 'running'` between turns: the devbox survives, so status alone
 * cannot tell a turn in flight from an agent sitting idle waiting for the next
 * message. Archiving that idle run is the ordinary triage gesture, and the
 * "still working" copy would be plain wrong for it. Waiting-for-input and
 * interrupted runs are not proceeding either, so they archive straight away
 * like terminal ones. When `activity` is omitted (callers that do not track
 * per-agent activity) the coarser status check stands in, preserving the
 * pre-activity behaviour.
 */
export function shouldConfirmArchive(
  agent: Pick<Agent, 'status'>,
  activity?: AgentActivity,
): boolean {
  if (activity) return isWorkingActivity(activity);
  return ACTIVE_STATUSES.has(agent.status);
}

/**
 * Broader "active" check for the stream view UI — includes completed and
 * interrupted agents because controls (input bar, header actions) should
 * remain visible for those states.
 */
export function isAgentStreamActive(agent: Pick<Agent, 'status'>): boolean {
  return NON_TERMINAL_STATUSES.has(agent.status) || agent.status === 'completed';
}

// ---------------------------------------------------------------------------
// Semantic status color class helpers (from @reflex/ui/client/status-colors)
// ---------------------------------------------------------------------------

import { badgeClass, dotClass } from '@reflex/ui/client/status-colors';

export { badgeClass, dotClass };

// ---------------------------------------------------------------------------
// Agent Activity (derived from stream events — more granular than status)
// ---------------------------------------------------------------------------

export const ACTIVITY_CONFIG: Record<
  AgentActivity,
  {
    label: string;
    className: string;
    /** Dot class for badge context (may include animate-pulse). */
    dot: string;
    /** Pure dot color class without animation — for standalone dot indicators. */
    dotColor: string;
    sidebar: { text: string; className: string };
  }
> = {
  starting: {
    label: 'Starting',
    className: badgeClass.warning,
    dot: `${dotClass.warning} animate-pulse`,
    dotColor: dotClass.warning,
    sidebar: { text: 'Starting…', className: 'text-status-warning/80' },
  },
  working: {
    label: 'Working',
    className: badgeClass.success,
    dot: `${dotClass.success} animate-pulse`,
    dotColor: dotClass.success,
    sidebar: { text: 'Working…', className: 'text-status-success/80' },
  },
  waiting_input: {
    label: 'Needs Input',
    className: `${badgeClass.warning} animate-pulse`,
    dot: `${dotClass.warning} animate-pulse`,
    dotColor: `${dotClass.warning} animate-pulse`,
    sidebar: { text: 'Needs input', className: 'text-status-warning' },
  },
  idle: {
    label: 'Idle',
    className: badgeClass.neutral,
    dot: dotClass.neutral,
    dotColor: dotClass.neutral,
    sidebar: { text: 'Idle', className: 'text-muted-foreground' },
  },
  interrupted: {
    label: 'Interrupted',
    className: badgeClass.warning,
    dot: dotClass.warning,
    dotColor: dotClass.warning,
    sidebar: { text: 'Interrupted', className: 'text-status-warning/80' },
  },
  completed: {
    label: 'Completed',
    className: badgeClass.info,
    dot: dotClass.info,
    dotColor: dotClass.info,
    sidebar: { text: 'Completed', className: 'text-status-info/80' },
  },
  error: {
    label: 'Error',
    className: badgeClass.error,
    dot: dotClass.error,
    dotColor: dotClass.error,
    sidebar: { text: 'Error', className: 'text-status-error/80' },
  },
  stopped: {
    label: 'Stopped',
    className: badgeClass.neutral,
    dot: dotClass.neutral,
    dotColor: dotClass.neutral,
    sidebar: { text: 'Stopped', className: 'text-muted-foreground' },
  },
};

/**
 * Whether an activity counts as "busy / actively generating" — a turn is in
 * flight and interrupting it is meaningful. This is the signal that gates the
 * Stop control: `idle`, `waiting_input`, `interrupted`, and every terminal
 * activity are *not* working, so Stop is hidden for them even while the devbox
 * stays live. Mirrors the chat input's generating state so the two never drift.
 */
export function isWorkingActivity(activity: AgentActivity): boolean {
  return activity === 'starting' || activity === 'working';
}

// ---------------------------------------------------------------------------
// Agent Status (server lifecycle state)
// ---------------------------------------------------------------------------

export const STATUS_CONFIG: Record<AgentStatus, { label: string; className: string; dot: string }> =
  {
    starting: { label: 'Starting', className: badgeClass.warning, dot: dotClass.warning },
    running: { label: 'Running', className: badgeClass.success, dot: dotClass.success },
    needs_input: {
      label: 'Needs Input',
      className: `${badgeClass.warning} animate-pulse`,
      dot: `${dotClass.warning} animate-pulse`,
    },
    completed: { label: 'Completed', className: badgeClass.info, dot: dotClass.info },
    interrupted: { label: 'Interrupted', className: badgeClass.warning, dot: dotClass.warning },
    stopping: { label: 'Stopping', className: badgeClass.orange, dot: dotClass.orange },
    stopped: { label: 'Stopped', className: badgeClass.neutral, dot: dotClass.neutral },
    error: { label: 'Error', className: badgeClass.error, dot: dotClass.error },
    terminated: { label: 'Terminated', className: badgeClass.neutral, dot: dotClass.neutral },
  };

// ---------------------------------------------------------------------------
// Blueprint Status
// ---------------------------------------------------------------------------

export const BLUEPRINT_STATUS_CONFIG: Record<
  BlueprintStatus,
  { label: string; className: string; sidebar: { text: string; className: string } }
> = {
  queued: {
    label: 'Queued',
    className: badgeClass.info,
    sidebar: { text: 'Queued', className: 'text-status-info/80' },
  },
  provisioning: {
    label: 'Provisioning',
    className: badgeClass.info,
    sidebar: { text: 'Provisioning...', className: 'text-status-info/80' },
  },
  building: {
    label: 'Building',
    className: badgeClass.info,
    sidebar: { text: 'Building...', className: 'text-status-info/80' },
  },
  build_complete: {
    label: 'Ready',
    className: badgeClass.success,
    sidebar: { text: 'Ready', className: 'text-status-success/80' },
  },
  failed: {
    label: 'Failed',
    className: badgeClass.error,
    sidebar: { text: 'Failed', className: 'text-status-error/80' },
  },
};

// ---------------------------------------------------------------------------
// Webhook Health
// ---------------------------------------------------------------------------

export const WEBHOOK_HEALTH_BADGE: Record<
  string,
  { label: string; className: string; dot: string }
> = {
  healthy: { label: 'Healthy', className: badgeClass.success, dot: dotClass.success },
  degraded: { label: 'Some errors', className: badgeClass.warning, dot: dotClass.warning },
  failing: { label: 'Failing', className: badgeClass.error, dot: dotClass.error },
  no_activity: { label: 'No activity', className: badgeClass.neutral, dot: dotClass.neutral },
};

// ---------------------------------------------------------------------------
// Plugin Status
// ---------------------------------------------------------------------------

export const PLUGIN_STATUS_BADGE: Record<
  string,
  { label: string; className: string; dot: string }
> = {
  active: { label: 'Active', className: badgeClass.success, dot: dotClass.success },
  disabled: { label: 'Disabled', className: badgeClass.warning, dot: dotClass.warning },
};

// ---------------------------------------------------------------------------
// Domain Claim Status (verified email-domain claims)
// ---------------------------------------------------------------------------

/** Shared by the org-admin and platform-admin domain-claim surfaces. */
export const DOMAIN_CLAIM_STATUS_BADGE: Record<
  BillingEntityDomainClaimStatus,
  { label: string; className: string; dot: string }
> = {
  pending: { label: 'Pending', className: badgeClass.warning, dot: dotClass.warning },
  verified: { label: 'Verified', className: badgeClass.success, dot: dotClass.success },
  lost: { label: 'Lost', className: badgeClass.error, dot: dotClass.error },
};

// ---------------------------------------------------------------------------
// Secret Source
// ---------------------------------------------------------------------------

export const SECRET_SOURCE_BADGE: Record<
  SecretSource,
  { label: string; className: string; dot: string }
> = {
  unset: { label: 'Not set', className: badgeClass.warning, dot: dotClass.warning },
  env: { label: 'Set via env', className: badgeClass.success, dot: dotClass.success },
  ui: { label: 'Set via UI', className: badgeClass.success, dot: dotClass.success },
  mount: { label: 'Set via mount', className: badgeClass.success, dot: dotClass.success },
  file: { label: 'Set via file', className: badgeClass.success, dot: dotClass.success },
};

// ---------------------------------------------------------------------------
// Inbox Item Status (summary labels for InboxSummaryCard)
// ---------------------------------------------------------------------------

export const INBOX_ITEM_STATUS: Record<string, { label: string; className: string }> = {
  needs_input: { label: 'Needs input', className: 'text-status-warning' },
  interrupted: { label: 'Interrupted', className: 'text-status-warning' },
  awaiting_approval: { label: 'Awaiting approval', className: 'text-status-warning' },
  idle: { label: 'Idle', className: 'text-muted-foreground' },
  error: { label: 'Error', className: 'text-status-error' },
  completed: { label: 'Completed', className: 'text-status-success' },
};

// ---------------------------------------------------------------------------
// Axon Event Types (re-exported from @reflex/ui/client/status-colors)
// ---------------------------------------------------------------------------

export {
  EVENT_BADGE_CLASS,
  EVENT_DOT_CLASS,
  DEFAULT_EVENT_BADGE_CLASS,
  DEFAULT_EVENT_DOT_CLASS,
} from '@reflex/ui/client/status-colors';

// ---------------------------------------------------------------------------
// Devbox Status (Runloop devbox lifecycle)
// ---------------------------------------------------------------------------

export const DEVBOX_STATUS_BADGE: Record<string, { label: string; className: string }> = {
  provisioning: { label: 'Provisioning', className: badgeClass.warning },
  initializing: { label: 'Initializing', className: badgeClass.warning },
  running: { label: 'Running', className: badgeClass.success },
  suspending: { label: 'Suspending', className: badgeClass.orange },
  suspended: { label: 'Suspended', className: badgeClass.neutral },
  resuming: { label: 'Resuming', className: badgeClass.warning },
  failure: { label: 'Failure', className: badgeClass.error },
  shutdown: { label: 'Shutdown', className: badgeClass.neutral },
  unknown: { label: 'Unknown', className: badgeClass.neutral },
};

// ---------------------------------------------------------------------------
// Sandbox State (derived from agent status for UI display)
// ---------------------------------------------------------------------------

export type SandboxState =
  | 'provisioning'
  | 'resuming'
  | 'active'
  | 'suspending'
  | 'suspended'
  | 'shutting_down'
  | 'shutdown'
  | 'error'
  | 'none';

/**
 * Map a raw Runloop devbox status string onto a {@link SandboxState}. Returns
 * `null` for `unknown` / unrecognised / empty values so callers can fall back
 * to the agent-status-derived state. The vocabulary mirrors
 * {@link DEVBOX_STATUS_BADGE}.
 */
function mapDevboxStatusToSandboxState(
  devboxStatus: string | null | undefined,
): SandboxState | null {
  switch (devboxStatus) {
    case 'running':
      return 'active';
    case 'provisioning':
    case 'initializing':
      return 'provisioning';
    case 'resuming':
      return 'resuming';
    case 'suspending':
      return 'suspending';
    case 'suspended':
      return 'suspended';
    case 'failure':
      return 'error';
    case 'shutdown':
      return 'shutdown';
    default:
      return null;
  }
}

/**
 * Map the agent lifecycle status onto the sandbox/devbox state shown in the
 * UI. Lives here (next to {@link SANDBOX_STATUS_CONFIG}) so both the status
 * badge and its hover preview derive the same state from the same inputs.
 *
 * When a live Runloop `devboxStatus` is supplied (from the
 * `/agents/:id/devbox-status` poll) and resolves to a known lifecycle, it
 * wins over the agent-status inference — that's the only signal that can
 * reveal an auto-suspended box, which the agent's `status` never reflects.
 * A `null` / `'unknown'` / unrecognised live status falls back to the
 * agent-status-derived state.
 */
export function deriveSandboxState(
  devboxId: string | null,
  agentStatus: AgentStatus,
  devboxStatus?: string | null,
): SandboxState {
  if (!devboxId) return 'none';
  const live = mapDevboxStatusToSandboxState(devboxStatus);
  if (live) return live;
  switch (agentStatus) {
    case 'starting':
      return 'provisioning';
    case 'running':
    case 'needs_input':
    case 'completed':
    case 'interrupted':
      // `interrupted` only kills the agent process — the devbox stays alive and
      // is resumable via a new message. The Runloop devbox may auto-suspend
      // separately, but we don't track that state here. See docs/architecture/agent-lifecycle.md.
      return 'active';
    case 'stopping':
      return 'shutting_down';
    case 'stopped':
    case 'terminated':
      return 'shutdown';
    case 'error':
      return 'error';
  }
}

export const SANDBOX_STATUS_CONFIG: Record<
  SandboxState,
  { label: string; dot: string; badge: string }
> = {
  provisioning: {
    label: 'Provisioning',
    dot: `${dotClass.warning} animate-pulse`,
    badge: badgeClass.warning,
  },
  resuming: {
    label: 'Resuming',
    dot: `${dotClass.warning} animate-pulse`,
    badge: badgeClass.warning,
  },
  active: {
    label: 'Active',
    dot: dotClass.success,
    badge: badgeClass.success,
  },
  suspending: {
    label: 'Suspending',
    dot: `${dotClass.orange} animate-pulse`,
    badge: badgeClass.orange,
  },
  suspended: {
    label: 'Suspended',
    dot: dotClass.warning,
    badge: badgeClass.warning,
  },
  shutting_down: {
    label: 'Shutting Down',
    dot: `${dotClass.orange} animate-pulse`,
    badge: badgeClass.orange,
  },
  shutdown: {
    label: 'Shut Down',
    dot: dotClass.neutral,
    badge: badgeClass.neutral,
  },
  error: {
    label: 'Error',
    dot: dotClass.error,
    badge: badgeClass.error,
  },
  none: {
    label: 'No Sandbox',
    dot: dotClass.neutral,
    badge: badgeClass.neutral,
  },
};

// ---------------------------------------------------------------------------
// Integration Run Status (re-exported from @reflex/ui/client/status-colors)
// ---------------------------------------------------------------------------

export { INTEGRATION_RUN_STATUS, getIntegrationRunStatus } from '@reflex/ui/client/status-colors';
